import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  Schedule,
  PersonAdd,
  Assignment,
  Business,
  CheckCircle
} from '@mui/icons-material'
import { AnalyticsService, RecentActivity } from '@shared/services/ApiClient'

function AdminActivity() {
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activities, setActivities] = useState<RecentActivity[]>([])

  useEffect(() => {
    loadActivities()
  }, [])

  const loadActivities = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await AnalyticsService.getRecentActivity()
      setActivities(data)
    } catch (err) {
      console.error('Failed to load activities:', err)
      setError('Failed to load activity log. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const getActivityIcon = (type: string) => {
    if (type.toLowerCase().includes('user') || type.toLowerCase().includes('invitation')) return <PersonAdd />
    if (type.toLowerCase().includes('ticket')) return <Assignment />
    if (type.toLowerCase().includes('organization') || type.toLowerCase().includes('organisation')) return <Business />
    return <CheckCircle />
  }

  const getActivityColor = (type: string) => {
    if (type.toLowerCase().includes('user') || type.toLowerCase().includes('invitation')) return theme.palette.success.main
    if (type.toLowerCase().includes('ticket')) return theme.palette.primary.main
    if (type.toLowerCase().includes('organization') || type.toLowerCase().includes('organisation')) return theme.palette.info.main
    return theme.palette.text.secondary
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Schedule sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Activity Log
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View all system activity including tickets, users, and organization changes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No activity to display
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <List>
              {activities.map((activity, index) => (
                <ListItem
                  key={activity.id}
                  sx={{
                    py: 2,
                    borderBottom: index < activities.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: alpha(getActivityColor(activity.type), 0.1),
                        color: getActivityColor(activity.type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {getActivityIcon(activity.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.message}
                    secondary={activity.timestamp}
                    primaryTypographyProps={{ variant: 'body1' }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default AdminActivity
